/**
 * Runtime Configuration Loader
 * Loads configuration from /public/config.json at application startup
 * NO HARDCODED VALUES - All config comes from this file
 */

export interface RuntimeConfig {
  apiGateway: {
    baseUrl: string;
    region: string;
  };
  cognito: {
    userPoolId: string;
    clientId: string;
    region: string;
  };
  environment: string;
  appName: string;
  version: string;
  logging: {
    enabled: boolean;
    level: 'debug' | 'info' | 'warn' | 'error';
    cloudWatchLogGroup: string;
    batchIntervalMs: number;
  };
  domain: {
    frontend: string;
    contactEmail: string;
    noreplyEmail: string;
  };
}

let cachedConfig: RuntimeConfig | null = null;
let configLoadPromise: Promise<RuntimeConfig> | null = null;

/**
 * Load runtime configuration from /config.json
 * Caches the result to avoid multiple fetches
 */
export async function loadRuntimeConfig(): Promise<RuntimeConfig> {
  // Return cached config if available
  if (cachedConfig) {
    return cachedConfig;
  }

  // Return existing promise if loading is in progress
  if (configLoadPromise) {
    return configLoadPromise;
  }

  configLoadPromise = fetch('/config.json', {
    cache: 'no-cache',
    headers: {
      'Cache-Control': 'no-cache',
    },
  })
    .then(response => {
      if (!response.ok) {
        throw new Error(`Failed to load config: ${response.status} ${response.statusText}`);
      }
      return response.json();
    })
    .then((config: RuntimeConfig) => {
      console.log('[Config] ✅ Loaded runtime configuration:', {
        apiGateway: config.apiGateway.baseUrl,
        cognito: config.cognito.userPoolId,
        environment: config.environment,
        version: config.version,
      });
      cachedConfig = config;
      (window as any).__SP_RUNTIME_CONFIG__ = config;
      return config;
    })
    .catch(error => {
      console.error('[Config] ❌ Failed to load runtime config:', error);
      console.error('[Config] ❌ CRITICAL: config.json is missing from /public directory');
      console.error('[Config] ❌ Application cannot start without runtime configuration');

      // Do NOT use fallback - force proper configuration
      throw new Error(
        'CONFIGURATION ERROR: config.json not found. ' +
        'The application requires a valid config.json file in the /public directory. ' +
        'This file must be deployed with the application and should never contain ' +
        'hardcoded credentials in source control.'
      );
    });

  return configLoadPromise;
}

/**
 * Get the cached runtime configuration
 * Throws an error if config hasn't been loaded yet
 */
export function getRuntimeConfig(): RuntimeConfig {
  if (!cachedConfig) {
    throw new Error('Runtime config not loaded. Call loadRuntimeConfig() first.');
  }
  return cachedConfig;
}

/**
 * Check if runtime configuration has been loaded
 */
export function isConfigLoaded(): boolean {
  return cachedConfig !== null;
}

/**
 * Get API Gateway base URL
 */
export function getApiBaseUrl(): string {
  return getRuntimeConfig().apiGateway.baseUrl;
}

/**
 * Get Cognito configuration
 */
export function getCognitoConfig() {
  return getRuntimeConfig().cognito;
}

/**
 * Get logging configuration
 */
export function getLoggingConfig() {
  return getRuntimeConfig().logging;
}

/**
 * Get environment name
 */
export function getEnvironment(): string {
  return getRuntimeConfig().environment;
}

/**
 * Check if we're in development mode
 */
export function isDevelopment(): boolean {
  return getEnvironment() === 'dev' || getEnvironment() === 'development';
}

/**
 * Check if we're in production mode
 */
export function isProduction(): boolean {
  return getEnvironment() === 'prod' || getEnvironment() === 'production';
}

/**
 * Get frontend domain URL
 * Returns safe default if config not loaded yet
 */
export function getFrontendDomain(): string {
  if (!cachedConfig) {
    return window.location.origin; // Safe fallback before config loads
  }
  return cachedConfig.domain.frontend;
}

/**
 * Get contact email address
 * Returns safe default if config not loaded yet
 */
export function getContactEmail(): string {
  if (!cachedConfig) {
    return 'admin@somethingproperty.co.za'; // Safe fallback before config loads
  }
  return cachedConfig.domain.contactEmail;
}

/**
 * Get noreply email address
 * Returns safe default if config not loaded yet
 */
export function getNoreplyEmail(): string {
  if (!cachedConfig) {
    return 'noreply@somethingproperty.co.za'; // Safe fallback before config loads
  }
  return cachedConfig.domain.noreplyEmail;
}
